import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter, Youtube, Linkedin } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { useSettings, digitsOnly } from "@/contexts/SettingsContext";

export default function Footer() {
  const s = useSettings();
  const socials = [
    { url: s.facebook_url, icon: Facebook, label: "Facebook", key: "facebook" },
    { url: s.instagram_url, icon: Instagram, label: "Instagram", key: "instagram" },
    { url: s.twitter_url, icon: Twitter, label: "Twitter", key: "twitter" },
    { url: s.youtube_url, icon: Youtube, label: "YouTube", key: "youtube" },
    { url: s.linkedin_url, icon: Linkedin, label: "LinkedIn", key: "linkedin" },
  ].filter((x) => x.url);
  return (
    <footer className="border-t border-slate-100 mt-16 bg-slate-50" data-testid="site-footer">
      <div className="container-x py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="One Hospitals" className="h-12 w-auto object-contain" />
          </div>
          <p className="text-sm leading-relaxed text-slate-600">A Unit of Hanshi Hospitals Pvt. Ltd. Premium multi-specialty care with 22+ specialties, 24/7 emergency response, and world-class diagnostics.</p>
          {socials.length > 0 && (
            <div className="flex items-center gap-3 mt-5">
              {socials.map((soc) => (
                <a key={soc.key} href={soc.url} target="_blank" rel="noreferrer" aria-label={soc.label} className="p-2 rounded-full transition bg-white border border-slate-200 hover:border-brand-purple" data-testid={`footer-${soc.key}`}>
                  <soc.icon className="h-4 w-4 text-slate-600" />
                </a>
              ))}
            </div>
          )}
        </div>
        <div>
          <h4 className="text-slate-900 font-semibold mb-4">Quick Links</h4>
          <ul className="space-y-2 text-sm text-slate-600">
            <li><Link to="/about" className="hover:text-brand-purple">About Us</Link></li>
            <li><Link to="/doctors" className="hover:text-brand-purple">Find a Doctor</Link></li>
            <li><Link to="/packages" className="hover:text-brand-purple">Health Packages</Link></li>
            <li><Link to="/insurance" className="hover:text-brand-purple">Insurance</Link></li>
            <li><Link to="/schemes" className="hover:text-brand-purple">Health Schemes</Link></li>
            <li><Link to="/resources" className="hover:text-brand-purple">Patient Resources</Link></li>
            <li><Link to="/careers" className="hover:text-brand-purple">Careers</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-slate-900 font-semibold mb-4">Patient Care</h4>
          <ul className="space-y-2 text-sm text-slate-600">
            <li><Link to="/book-appointment" className="hover:text-brand-purple">Book Appointment</Link></li>
            <li><Link to="/second-opinion" className="hover:text-brand-purple">Get Second Opinion</Link></li>
            <li><Link to="/specialties" className="hover:text-brand-purple">Specialties</Link></li>
            <li><Link to="/services" className="hover:text-brand-purple">Services</Link></li>
            <li><Link to="/gallery" className="hover:text-brand-purple">Gallery</Link></li>
            <li><Link to="/contact" className="hover:text-brand-purple">Contact Us</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-slate-900 font-semibold mb-4">Reach Us</h4>
          <ul className="space-y-3 text-sm text-slate-600">
            <li className="flex gap-3" data-testid="footer-address"><MapPin className="h-4 w-4 mt-0.5 shrink-0 text-brand-green" /><span>{s.address}</span></li>
            <li className="flex gap-3" data-testid="footer-phone"><Phone className="h-4 w-4 mt-0.5 shrink-0 text-brand-green" /><a href={`tel:${digitsOnly(s.phone)}`} className="hover:text-brand-purple">{s.phone}</a></li>
            <li className="flex gap-3" data-testid="footer-emergency"><Phone className="h-4 w-4 mt-0.5 shrink-0 text-red-500" /><a href={`tel:${digitsOnly(s.emergency_phone)}`} className="hover:text-brand-purple font-semibold">Emergency: {s.emergency_phone}</a></li>
            <li className="flex gap-3" data-testid="footer-email"><Mail className="h-4 w-4 mt-0.5 shrink-0 text-brand-green" /><a href={`mailto:${s.email}`} className="hover:text-brand-purple">{s.email}</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-100">
        <div className="container-x py-5 flex flex-col md:flex-row justify-between gap-3 text-xs text-mute">
          <p>© {new Date().getFullYear()} {s.hospital_name}. All rights reserved.</p>
          <p className="flex gap-4"><a href="#" className="hover:text-brand-purple">Privacy Policy</a><a href="#" className="hover:text-brand-purple">Terms &amp; Conditions</a><a href="#" className="hover:text-brand-purple">Disclaimer</a></p>
        </div>
      </div>
    </footer>
  );
}
